import { ChangeDetectionStrategy, Component, Input } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { DynamicScriptService } from '@core/services/dynamic-script.service';
import { SectionState } from '@shared-features/enums/layout.enum';
import { LazyLibId, LazyLibURL } from '@shared-features/enums/shared-enums.enum';
import { LoadingService } from '@shared/services/loading.service';
import { AsyncFeedbackService } from '@shared/services/async-feedback.service';

@Component({
  selector: 'app-print-sections',
  templateUrl: './print-sections.component.html',
  styleUrls: ['./print-sections.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PrintSectionsComponent {
  @Input() sectionIds: string[];
  @Input() fileName: string;
  @Input() titleElementId?: string;
  @Input() printTitle: string;

  private jsPDF: any;

  constructor(
    private dynamicScriptService: DynamicScriptService,
    private loadingService: LoadingService,
    private translateService: TranslateService,
    private asyncFeedbackService: AsyncFeedbackService
  ) {}

  async print(): Promise<void> {
    const sections = (this.sectionIds || []).map((sectionId) => document.getElementById(sectionId)).filter((section) => !!section);

    if (!sections.length) {
      console.warn('[PrintSectionsComponent]: there is no sections to print');
      return;
    }

    const titleElement = document.getElementById(this.titleElementId) || document.createElement('span');
    const currentTitle = titleElement?.innerHTML || '';

    this.loadingService.startLoading(SectionState.LoadingTransparent);

    try {
      const { jsPDF } = await this.dynamicScriptService
        .loadDynamicScript(LazyLibId.jspdf, LazyLibURL.jspdf)
        .then(() => (window as any).jspdf);

      this.jsPDF = new jsPDF('p', 'mm', 'a4', true);
      titleElement.innerHTML = this.printTitle || currentTitle;

      // Sequential, not Promise.all: pages share the same jsPDF instance, so adding them
      // out of completion order (rather than section order) would scramble the PDF.
      for (let i = 0; i < sections.length; i++) {
        await this.addPageToPdf(sections[i], i < sections.length - 1);
      }

      this.jsPDF.save(`${this.fileName}.pdf`);
    } catch (err) {
      this.asyncFeedbackService.showFailureMessage(this.translateService.instant('ErrorMessages.ErrorHappened'));
    } finally {
      titleElement.innerHTML = currentTitle;
      this.loadingService.stopLoading();
    }
  }

  private async addPageToPdf(page, addTrailingPageBreak: boolean, width?: number): Promise<void> {
    if (!page) {
      return;
    }

    const html2canvas = await this.dynamicScriptService
      .loadDynamicScript(LazyLibId.html2canvas, LazyLibURL.html2canvas)
      .then(() => (window as any).html2canvas);

    const canvas = await html2canvas(page);
    const imageWidth = width || 200;
    const imageHeight = (canvas.height * imageWidth) / canvas.width;
    const contentDataURL = canvas.toDataURL('image/png');
    this.jsPDF.addImage(contentDataURL, 'PNG', 5, 15, imageWidth, imageHeight, '', 'SLOW');
    if (addTrailingPageBreak) {
      this.jsPDF.addPage();
    }
  }
}
